import queryString from 'query-string'
import { SelectOpcao } from 'src/componentes/unform/select/tipos'
import { TipoOrdenacao } from 'src/tipos'

import { gatewayApi } from '../instancias-api'

const urlBase = '/rescisaomatricula'
const urlPortal = `${urlBase}/portal`

/* ==========================================================================
 * Tipos (espelham os DTOs do backend — Ipgs.Educacional.Application/Queries/Rescisoes)
 * ======================================================================== */

/** Espelha ProtocoloRescisaoListaDto. */
export interface ProtocoloRescisaoListaDto {
  id: string
  numeroProtocolo: string
  nomeAluno: string
  cpf: string
  dataAbertura: string
  dataFimProtocolo?: string
  status: string
  setorResponsavel?: string
  motivo?: string
  modalidade?: string
  curso?: string
}

/** Espelha ListarProtocolosRescisaoQueryResult. */
export interface ListarProtocolosRescisaoResposta {
  itens: ProtocoloRescisaoListaDto[]
  quantidade: number
  temProximaPagina: boolean
}

export interface ObservacaoRescisaoDto {
  id: string
  usuarioId: string
  nomeUsuario: string
  texto: string
  dataHora: string
}

export interface HistoricoStatusRescisaoDto {
  id: string
  statusAnterior?: string
  statusNovo: string
  usuarioId?: string
  nomeUsuario?: string
  dataHora: string
  ip?: string
}

/** Espelha ObterProtocoloRescisaoPorIdQueryResult (3 blocos). */
export interface ProtocoloRescisaoDetalheDto {
  // Bloco Gerais
  id: string
  numeroProtocolo: string
  alunoId: string
  nomeAluno: string
  cpf: string
  email?: string
  contato?: string
  matriculaId?: string
  modalidade?: string
  curso?: string
  status: string
  setorResponsavel?: string
  motivo?: string
  motivoId?: string
  motivoDetalhado?: string
  motivoOutroTexto?: string
  casoEspecial: boolean
  dataAbertura: string
  dataFimProtocolo?: string
  dataPrimeiroContato?: string
  dataUltimaAlteracao: string

  // Bloco Financeiro (nullable — calculado pelo motor de cálculo)
  parcelamento?: number
  formaPagamentoCurso?: string
  nrParcelasPagas?: number
  valoresPagos?: number
  nrParcelasPendentes?: number
  valoresPendentes?: number
  nrParcelasAVencer?: number
  valoresAVencer?: number
  percentualTaxaRescisao?: number
  multaRescisoria?: number
  totalAPagar?: number
  formaPagamentoRescisao?: string
  statusParcelaRescisao?: string
  cobrancaRescisaoId?: string

  // Bloco Controles
  observacoes: ObservacaoRescisaoDto[]
  historicoStatus: HistoricoStatusRescisaoDto[]
}

/** Espelha MotivoRescisaoDto. */
export interface MotivoRescisaoDto {
  id: string
  descricao: string
  setor: string
  ordem: number
}

/** Espelha AbrirProtocoloRescisaoCommandResult. */
export interface AbrirProtocoloRescisaoResposta {
  id: string
  numeroProtocolo: string
}

export interface FiltrosListagemRescisao {
  nomeAluno?: string
  cpf?: string
  setor?: string
  status?: string[]
  numeroProtocolo?: string
  dataAberturaInicio?: string
  dataAberturaFim?: string
}

export interface ElegibilidadeRescisao {
  podeAbrir: boolean
  motivoBloqueio?: string
}

/** Espelha ObterHistoricoRescisaoQueryResult. */
export interface HistoricoRescisaoItemDto {
  nomeUsuario: string
  statusAnterior?: string
  statusNovo: string
  dataHora: string
  ip?: string
}

export interface HistoricoRescisaoResposta {
  numeroProtocolo: string
  itens: HistoricoRescisaoItemDto[]
}

/** Espelha ObterValoresRescisaoQueryResult. */
export interface ValoresRescisaoDto {
  nrParcelasPagas: number
  valoresPagos: number
  nrParcelasPendentes: number
  valoresPendentes: number
  nrParcelasAVencer: number
  valoresAVencer: number
  percentualTaxaRescisao: number
  multaRescisoria: number
  totalAPagar: number
  cursoQuitado: boolean
}

export interface AtualizarDadosGeraisRescisao {
  status: string
  setor?: string
  motivoId?: string
  motivoDetalhado: string
  motivoOutroTexto?: string
  casoEspecial: boolean
  dataPrimeiroContato?: string
}

export interface AtualizarDadosFinanceirosRescisao {
  formaPagamentoRescisao?: string
  statusParcelaRescisao?: string
  percentualTaxaRescisao?: number
  valorFinalRescisao?: number
}

/** Espelha ObterProtocoloAtivoDoAlunoQueryResult. */
export interface ProtocoloAtivoAlunoDto {
  id: string
  numeroProtocolo: string
  status: string
  aceitouTermosIniciais: boolean
  matriculaId?: string
  motivoId?: string
  motivoDetalhado?: string
  motivoOutroTexto?: string
  telefone?: string
  percentualTaxaRescisao: number
}

/** Espelha ProtocoloRescisaoAlunoDto — item da listagem "meus pedidos" do portal do aluno. */
export interface ProtocoloRescisaoAlunoDto {
  id: string
  numeroProtocolo: string
  status: string
  dataAbertura: string
  dataFimProtocolo?: string
  motivo?: string
  curso?: string
  modalidade?: string
}

/** Espelha MatriculaAtivaDto. */
export interface MatriculaAtivaAlunoDto {
  id: string
  curso: string
  modalidade: string
  turma: string
}

export interface DadosSolicitacaoRescisao {
  matriculaId: string
  motivoId: string
  motivoDetalhado: string
  motivoOutroTexto?: string
  telefone: string
}

/** Espelha AceitarTermoRescisaoCommandResult. */
export interface AceitarTermoRescisaoResposta {
  sucesso: boolean
}

/* ==========================================================================
 * Endpoints Admin — RescisaoMatriculaController
 * ======================================================================== */

export const ListarProtocolosRescisao = async (
  filtros: FiltrosListagemRescisao,
  pagina: number,
  tamanhoPagina: number,
  chaveOrdenacao?: string,
  ordem?: TipoOrdenacao
): Promise<ListarProtocolosRescisaoResposta | null> => {
  const query = queryString.stringify(
    {
      NomeAluno: filtros.nomeAluno,
      Cpf: filtros.cpf ? filtros.cpf.replace(/[.-]/g, '') : undefined,
      Setor: filtros.setor,
      Status: filtros.status,
      NumeroProtocolo: filtros.numeroProtocolo,
      DataAberturaInicio: filtros.dataAberturaInicio,
      DataAberturaFim: filtros.dataAberturaFim,
      Pagina: pagina,
      TamanhoPagina: tamanhoPagina,
      ChaveOrdenacao: chaveOrdenacao,
      Ordem: ordem
    },
    { skipNull: true, skipEmptyString: true }
  )

  const { data } = await gatewayApi.get<ListarProtocolosRescisaoResposta>(
    `${urlBase}?${query}`
  )

  return data
}

export const ObterProtocoloRescisaoPorId = async (
  id: string
): Promise<ProtocoloRescisaoDetalheDto> => {
  const { data } = await gatewayApi.get<ProtocoloRescisaoDetalheDto>(
    `${urlBase}/${id}`
  )

  if (!data) {
    throw new Error('Protocolo de rescisão não encontrado.')
  }

  return data
}

export const ListarMotivosRescisao = async (): Promise<MotivoRescisaoDto[]> => {
  const { data } = await gatewayApi.get<MotivoRescisaoDto[]>(
    `${urlBase}/motivos`
  )

  return data ?? []
}

/** Motivos formatados como opções de select (para o wizard TELA 3). */
export const ListarMotivosRescisaoOpcoes = async (): Promise<SelectOpcao[]> => {
  const motivos = await ListarMotivosRescisao()
  return motivos.map(m => ({ id: m.id, texto: m.descricao }))
}

export const AbrirProtocoloRescisao = async (
  alunoId: string,
  setorResponsavel?: string,
  dataPrimeiroContato?: string
): Promise<AbrirProtocoloRescisaoResposta> => {
  const { data } = await gatewayApi.post<AbrirProtocoloRescisaoResposta>(
    urlBase,
    {
      alunoId,
      setorResponsavel: setorResponsavel || undefined,
      dataPrimeiroContato: dataPrimeiroContato || undefined
    }
  )

  return data
}

/** Confirma o envio do formulário: dispara o e-mail com o link do wizard e move o status. */
export const EnviarFormularioRescisao = async (id: string): Promise<void> => {
  await gatewayApi.post(`${urlBase}/${id}/enviar-formulario`)
}

export const VerificarElegibilidadeRescisao = async (
  alunoId: string
): Promise<ElegibilidadeRescisao> => {
  const { data } = await gatewayApi.get<ElegibilidadeRescisao>(
    `${urlBase}/elegibilidade?alunoId=${alunoId}`
  )

  return data
}

export const AtualizarDadosGeraisRescisao = async (
  id: string,
  dados: AtualizarDadosGeraisRescisao
): Promise<void> => {
  await gatewayApi.put(`${urlBase}/${id}/gerais`, dados)
}

export const AtualizarDadosFinanceirosRescisao = async (
  id: string,
  dados: AtualizarDadosFinanceirosRescisao
): Promise<void> => {
  await gatewayApi.put(`${urlBase}/${id}/financeiro`, dados)
}

export const AdicionarObservacaoInterna = async (
  id: string,
  texto: string
): Promise<void> => {
  await gatewayApi.post(`${urlBase}/${id}/observacoes`, { texto })
}

export const ObterHistoricoRescisao = async (
  id: string
): Promise<HistoricoRescisaoResposta> => {
  const { data } = await gatewayApi.get<HistoricoRescisaoResposta>(
    `${urlBase}/${id}/historico`
  )

  return data
}

export const ObterValoresRescisao = async (
  id: string
): Promise<ValoresRescisaoDto> => {
  const { data } = await gatewayApi.get<ValoresRescisaoDto>(
    `${urlBase}/${id}/valores`
  )

  return data
}

export const ReverterRescisao = async (id: string): Promise<void> => {
  await gatewayApi.post(`${urlBase}/${id}/reverter`)
}

/** Ação manual do financeiro: gera a cobrança de taxa de rescisão (só após o aluno aceitar o Termo).
 * A forma de pagamento não é mais escolhida na geração — o aluno decide ao pagar (boleto/PIX/cartão)
 * no painel financeiro. `formaPagamento`/`numeroParcelas` continuam no contrato do backend (opcionais,
 * ignorados) para não quebrar o command existente. */
export interface GerarCobrancaRescisaoPayload {
  valor: number
  formaPagamento?: string
  numeroParcelas?: number
  dataVencimento: Date | string
  permitirParcelamento?: boolean
}

export const GerarCobrancaRescisao = async (
  id: string,
  dados: GerarCobrancaRescisaoPayload
): Promise<void> => {
  await gatewayApi.post(`${urlBase}/${id}/gerar-cobranca`, dados)
}

/** Prévia do PDF do termo (sempre recalculada), exibida no modal antes do envio efetivo. */
export const ObterTermoRescisaoPrevia = async (id: string): Promise<string> => {
  const { data } = await gatewayApi.get(`${urlBase}/${id}/termo-previa`, {
    headers: {
      Accept: 'application/pdf'
    },
    responseType: 'blob'
  })

  return URL.createObjectURL(data)
}

/** Confirma o envio do termo dentro do modal: move o status e dispara o e-mail ao aluno. */
export const EnviarTermoRescisao = async (id: string): Promise<void> => {
  await gatewayApi.post(`${urlBase}/${id}/enviar-termo-rescisao`)
}

/** Recalcula o snapshot financeiro (bloco Financeiro) a partir das cobranças atuais da matrícula. */
export const RecalcularValoresRescisao = async (id: string): Promise<void> => {
  await gatewayApi.post(`${urlBase}/${id}/recalcular-valores`)
}

/* ==========================================================================
 * Endpoints Portal do Aluno — RescisaoMatriculaPortalAlunoController
 * O backend resolve o aluno pelo usuário autenticado — nenhuma função abaixo
 * recebe alunoId/usuarioId.
 * ======================================================================== */

/**
 * Protocolo do wizard. `protocoloId` (o id da URL, vindo do link do e-mail) é opcional: como o
 * aluno pode ter mais de um protocolo ativo — um por curso —, informá-lo garante que o wizard
 * abra o pedido certo. Sem ele, o backend devolve o protocolo ativo que ainda falta preencher.
 */
export const ObterProtocoloAtivoDoAluno = async (
  protocoloId?: string
): Promise<ProtocoloAtivoAlunoDto | null> => {
  const { data } = await gatewayApi.get<ProtocoloAtivoAlunoDto>(
    `${urlPortal}/ativo`,
    { params: protocoloId ? { protocoloId } : undefined }
  )

  return data ?? null
}

/** Lista todos os pedidos de rescisão do aluno logado (para a tela de Requerimentos). */
export const ListarMeusPedidosRescisao = async (): Promise<
  ProtocoloRescisaoAlunoDto[]
> => {
  const { data } = await gatewayApi.get<ProtocoloRescisaoAlunoDto[]>(
    `${urlPortal}/meus-pedidos`
  )

  return data ?? []
}

// protocoloId: quando a matrícula já está vinculada a ESSE protocolo, ela continua aparecendo na
// lista mesmo estando em "matrícula com protocolo ativo" — necessário para reabrir a TELA 3
// (Dados da solicitação) já preenchida sem o select ficar vazio.
export const ListarMatriculasAtivasAluno = async (
  protocoloId?: string
): Promise<SelectOpcao[]> => {
  const { data } = await gatewayApi.get<MatriculaAtivaAlunoDto[]>(
    `${urlPortal}/matriculas-ativas`,
    { params: protocoloId ? { protocoloId } : undefined }
  )

  return (data ?? [])
    .filter(m => m.curso && m.turma)
    .map(m => ({
      id: m.id,
      texto: `${m.curso} — ${m.turma} (${m.modalidade})`
    }))
}

export const AceitarTermosIniciais = async (id: string): Promise<void> => {
  await gatewayApi.post(`${urlPortal}/${id}/aceitar-termos-iniciais`)
}

/** Motivos ativos, para o select de motivo da TELA 3 do wizard do aluno. */
export const ListarMotivosRescisaoOpcoesAluno = async (): Promise<
  SelectOpcao[]
> => {
  const { data } = await gatewayApi.get<MotivoRescisaoDto[]>(
    `${urlPortal}/motivos`
  )

  return (data ?? []).map(m => ({ id: m.id, texto: m.descricao }))
}

/** Valores calculados do protocolo, para a TELA 2 do wizard do aluno. */
export const ObterValoresRescisaoAluno = async (
  id: string
): Promise<ValoresRescisaoDto> => {
  const { data } = await gatewayApi.get<ValoresRescisaoDto>(
    `${urlPortal}/${id}/valores`
  )

  return data
}

export const PreencherDadosSolicitacao = async (
  id: string,
  dados: DadosSolicitacaoRescisao
): Promise<void> => {
  await gatewayApi.put(`${urlPortal}/${id}/dados-solicitacao`, dados)
}

/** Conclusão da Tela Valores do wizard do aluno — move o protocolo para Em contato Acadêmico/Financeiro. */
export const ConcluirSolicitacaoRescisao = async (id: string): Promise<void> => {
  await gatewayApi.post(`${urlPortal}/${id}/concluir-solicitacao`)
}

/** Retorna a URL do blob do PDF do Termo de Rescisão. */
export const ObterTermoRescisao = async (id: string): Promise<string> => {
  const { data } = await gatewayApi.get(`${urlPortal}/${id}/termo`, {
    headers: {
      Accept: 'application/pdf'
    },
    responseType: 'blob'
  })

  return URL.createObjectURL(data)
}

export const AceitarTermoRescisao = async (
  id: string
): Promise<AceitarTermoRescisaoResposta> => {
  const { data } = await gatewayApi.post<AceitarTermoRescisaoResposta>(
    `${urlPortal}/${id}/aceitar-termo-rescisao`
  )

  return data
}

export const CancelarRescisaoAoVerTermo = async (
  id: string,
  motivoCancelamento: string
): Promise<void> => {
  await gatewayApi.post(`${urlPortal}/${id}/cancelar-ao-ver-termo`, {
    motivoCancelamento
  })
}
